import React from "react";
import { useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { FieldArray } from "react-final-form-arrays";
import { Dialog } from "@syos-music/common";
import {
  Fieldset,
  TextField,
  SelectField,
  DateTimeField,
} from "@syos-music/form";

import {
  Button,
  Grid,
  MenuItem,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Plus as AddIcon, Delete as DeleteIcon } from "mdi-material-ui";

import PriceField from "components/form/PriceField";

import CartItemConstraintField from "./CartItemConstraintField";

const MUTATION = gql`
  mutation (
    $code: String!
    $type: DiscountCodeType!
    $values: [PriceInput]
    $percentage: Float
    $usageLeft: Int
    $minimumAmounts: [PriceInput]
    $startDate: Date
    $endDate: Date
    $constraints: DiscountCodeConstraintsInput
  ) {
    CreateDiscountCode(
      code: $code
      type: $type
      values: $values
      percentage: $percentage
      usageLeft: $usageLeft
      minimumAmounts: $minimumAmounts
      startDate: $startDate
      endDate: $endDate
      constraints: $constraints
    ) {
      discountCode {
        id
      }
    }
  }
`;

export default function CreateDiscountCode({ onSubmitted, children }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);

  const [createDiscountCode] = useMutation(MUTATION);

  return (
    <>
      {children({ isOpen, open, close })}

      <Dialog open={isOpen} onClose={close} maxWidth="md">
        <Form
          mutators={{ ...arrayMutators }}
          onSubmit={async (values) => {
            const constraints =
              values.constraints.list.length > 0
                ? {
                    ...values.constraints,
                    list: values.constraints.list.map((constraint) => {
                      let cloned = JSON.parse(JSON.stringify(constraint));
                      if (cloned.type === "product")
                        cloned.product.product = cloned.product.product.id;

                      return cloned;
                    }),
                  }
                : null;

            return createDiscountCode({
              variables: {
                ...values,
                minimumAmount: parseFloat(values.minimumAmount),
                constraints,
              },
            })
              .then(({ data }) => {
                onSubmitted(data.CreateDiscountCode.discountCode);
                close();
              })
              .catch(() => true);
          }}
          initialValues={{
            type: "percentage",
            values: [
              { currency: "EUR", value: 0 },
              { currency: "USD", value: 0 },
            ],
            percentage: 0,
            minimumAmounts: [
              { currency: "EUR", value: 0 },
              { currency: "USD", value: 0 },
            ],
            constraints: {
              list: [],
              combinator: "AND",
            },
          }}
        >
          {({ handleSubmit, submitting, values }) => {
            return (
              <form onSubmit={handleSubmit} noValidate autoComplete="off">
                <Dialog.Title color="primary">
                  Créer un code de réduction
                </Dialog.Title>

                <Dialog.Content>
                  <Grid container>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="code"
                        required
                        label="Code de réduction"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name="type"
                        label="Type de réduction"
                        required
                      >
                        <MenuItem value="fixed">Montant fixe</MenuItem>
                        <MenuItem value="percentage">Pourcentage</MenuItem>
                      </SelectField>
                    </Grid>

                    {values.type === "fixed" && (
                      <Grid item xs={12}>
                        <Fieldset
                          legend="Montants"
                          name="values"
                          collapsible={false}
                        >
                          <FieldArray name="values">
                            {({ fields }) => (
                              <Grid container>
                                {fields.map((fieldName, idx) => (
                                  <Grid key={idx} item xs={12} sm={6}>
                                    <PriceField name={fieldName} required />
                                  </Grid>
                                ))}
                              </Grid>
                            )}
                          </FieldArray>
                        </Fieldset>
                      </Grid>
                    )}

                    {values.type === "percentage" && (
                      <Grid item xs={12}>
                        <TextField
                          name="percentage"
                          type="number"
                          min={0}
                          max={100}
                          required
                          label="Pourcentage"
                          parse={(value) => (parseFloat(value) || 0) / 100}
                          format={(value) => (parseFloat(value) || 0) * 100}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">%</InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Fieldset
                        legend="Utilisations restantes"
                        collapsible={false}
                      >
                        <TextField
                          type="number"
                          name="usageLeft"
                          clearable
                          InputProps={{ inputProps: { step: 1, min: 1 } }}
                        />
                      </Fieldset>
                    </Grid>

                    <Grid item xs={12}>
                      <Fieldset
                        legend="Montants minimums (HT)"
                        name="minimumAmounts"
                        collapsible={false}
                      >
                        <FieldArray name="minimumAmounts">
                          {({ fields }) => (
                            <Grid container>
                              {fields.map((fieldName, idx) => (
                                <Grid key={idx} item xs={12} sm={6}>
                                  <PriceField name={fieldName} required />
                                </Grid>
                              ))}
                            </Grid>
                          )}
                        </FieldArray>
                      </Fieldset>
                    </Grid>

                    <Grid item xs={12}>
                      <Fieldset legend="Dates" collapsible={false}>
                        <Grid container>
                          <Grid item xs={12} sm={6}>
                            <DateTimeField
                              name="startDate"
                              label="Début"
                              parse={(value) => value ?? null}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <DateTimeField
                              name="endDate"
                              label="Fin"
                              parse={(value) => value ?? null}
                            />
                          </Grid>
                        </Grid>
                      </Fieldset>
                    </Grid>

                    <Grid item xs={12}>
                      <FieldArray name="constraints.list">
                        {({ fields, ...rest }) => (
                          <Fieldset
                            legend={
                              <Grid container spacing={1} alignItems="center">
                                <Grid item>Contraintes</Grid>

                                <Grid item>
                                  <SelectField name="constraints.combinator">
                                    <MenuItem value="AND">ET</MenuItem>
                                    <MenuItem value="OR">OU</MenuItem>
                                  </SelectField>
                                </Grid>
                              </Grid>
                            }
                            collapsible={false}
                          >
                            <Grid container>
                              {fields.map((fieldName, idx) => (
                                <Grid item xs={12} key={fieldName}>
                                  <Grid container alignItems="center">
                                    <Grid item xs>
                                      <CartItemConstraintField
                                        name={fieldName}
                                      />
                                    </Grid>
                                    <Grid item>
                                      <IconButton
                                        size="small"
                                        onClick={() => fields.remove(idx)}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              ))}

                              <Grid item xs={12} align="right">
                                <Button
                                  startIcon={<AddIcon />}
                                  onClick={() => fields.push({ type: null })}
                                  size="small"
                                >
                                  Ajouter
                                </Button>
                              </Grid>
                            </Grid>
                          </Fieldset>
                        )}
                      </FieldArray>
                    </Grid>
                  </Grid>
                </Dialog.Content>

                <Dialog.Actions>
                  <Button variant="text" onClick={close}>
                    Annuler
                  </Button>

                  <LoadingButton type="submit" loading={submitting}>
                    Créer
                  </LoadingButton>
                </Dialog.Actions>
              </form>
            );
          }}
        </Form>
      </Dialog>
    </>
  );
}

CreateDiscountCode.defaultProps = {
  onSubmitted: () => {},
};
